import type { SlotImage, SourceRotation } from '../types';
import { fitImageToPanel } from './geometry';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const isAcceptedImage = (file: File) => ACCEPTED_TYPES.includes(file.type);

const decodeBitmap = async (source: File | Blob): Promise<ImageBitmap> => {
  try {
    return await createImageBitmap(source);
  } catch (err) {
    const name = source instanceof File ? source.name : 'image';
    throw new Error(
      `Could not decode ${name}. Try a JPG, PNG, or WebP. (HEIC isn't supported — convert it to JPG first.) Details: ${(err as Error).message}`
    );
  }
};

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;
type AnyCtx = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

interface CanvasBundle {
  canvas: AnyCanvas;
  ctx: AnyCtx;
  toBlob: (type: string, quality?: number) => Promise<Blob>;
}

const getCanvas = (width: number, height: number): CanvasBundle => {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d')!;
    return {
      canvas,
      ctx,
      toBlob: (type, quality) => canvas.convertToBlob({ type, quality }),
    };
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  return {
    canvas,
    ctx,
    toBlob: (type, quality) =>
      new Promise((resolve, reject) =>
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
          type,
          quality
        )
      ),
  };
};

interface DownsampleOptions {
  /** 0..1 normalized horizontal crop offset, in oriented coords. Default 0.5. */
  cropX?: number;
  /** 0..1 normalized vertical crop offset, in oriented coords. Default 0.5. */
  cropY?: number;
  /** CW rotation applied to the source before cropping. Default 0. */
  rotation?: SourceRotation;
  /** Mirror horizontally (across the vertical axis), applied after rotation. */
  flipH?: boolean;
  /** Mirror vertically (across the horizontal axis), applied after rotation. */
  flipV?: boolean;
  /** When re-cropping an already-uploaded slot, pass the original source Blob
   *  instead of the (cropped) blob currently sitting in the slot. */
  sourceBlob?: Blob;
  /** When true: skip resampling. Crop only — the resulting JPEG keeps the
   *  cropped region's native source resolution. Bigger PDF, more detail
   *  preserved for fine printing. Default false (resample to panel dims). */
  preserveResolution?: boolean;
}

/** Build an intermediate canvas holding the source after rotation + flip
 *  applied around its center. Returns null when no transform is needed
 *  (caller can use the bitmap directly). */
const buildOrientedCanvas = (
  bitmap: ImageBitmap,
  rotation: SourceRotation,
  flipH: boolean,
  flipV: boolean
): AnyCanvas | null => {
  if (rotation === 0 && !flipH && !flipV) return null;
  const isQuarterTurn = rotation === 90 || rotation === 270;
  const orientedW = isQuarterTurn ? bitmap.height : bitmap.width;
  const orientedH = isQuarterTurn ? bitmap.width : bitmap.height;
  const { canvas, ctx } = getCanvas(orientedW, orientedH);
  ctx.save();
  ctx.translate(orientedW / 2, orientedH / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  ctx.restore();
  return canvas;
};

// Crop + downsample source to exact panel dimensions, then encode as JPEG.
// Keeps memory bounded regardless of how huge the user's source image is.
// The original source Blob is retained on the returned SlotImage so the slot
// can be re-cropped later (e.g. on orientation change or via CropModal).
export const downsampleToPanel = async (
  file: File | Blob,
  panelWidth: number,
  panelHeight: number,
  options: DownsampleOptions = {}
): Promise<SlotImage> => {
  const cropX = options.cropX ?? 0.5;
  const cropY = options.cropY ?? 0.5;
  const rotation = options.rotation ?? 0;
  const flipH = options.flipH ?? false;
  const flipV = options.flipV ?? false;
  const preserveResolution = options.preserveResolution ?? false;
  const source = options.sourceBlob ?? file;

  const bitmap = await decodeBitmap(file);
  const sourceDims = { width: bitmap.width, height: bitmap.height };

  // Pipeline: source → (optional) rotation + flip → crop → resample (optional).
  const oriented = buildOrientedCanvas(bitmap, rotation, flipH, flipV);
  const isQuarterTurn = rotation === 90 || rotation === 270;
  const orientedW = isQuarterTurn ? bitmap.height : bitmap.width;
  const orientedH = isQuarterTurn ? bitmap.width : bitmap.height;

  const { sx, sy, sWidth, sHeight } = fitImageToPanel(
    orientedW,
    orientedH,
    panelWidth,
    panelHeight,
    cropX,
    cropY
  );

  const outW = preserveResolution ? sWidth : panelWidth;
  const outH = preserveResolution ? sHeight : panelHeight;

  const { ctx, toBlob } = getCanvas(outW, outH);
  ctx.drawImage(oriented ?? bitmap, sx, sy, sWidth, sHeight, 0, 0, outW, outH);
  bitmap.close();

  const blob = await toBlob('image/jpeg', 0.92);
  const previewUrl = URL.createObjectURL(blob);
  return {
    source,
    blob,
    width: sourceDims.width,
    height: sourceDims.height,
    cropX,
    cropY,
    rotation,
    flipH,
    flipV,
    previewUrl,
  };
};

export interface RecropOverrides {
  cropX?: number;
  cropY?: number;
  rotation?: SourceRotation;
  flipH?: boolean;
  flipV?: boolean;
  preserveResolution?: boolean;
}

// Re-crop an existing slot at new panel dimensions, optionally overriding any
// of cropX/cropY/rotation/flipH/flipV/preserveResolution. Anything not given
// in the overrides is inherited from the slot's current state.
export const recropSlot = async (
  current: SlotImage,
  panelWidth: number,
  panelHeight: number,
  overrides: RecropOverrides = {}
): Promise<SlotImage> => {
  return downsampleToPanel(current.source, panelWidth, panelHeight, {
    cropX: overrides.cropX ?? current.cropX,
    cropY: overrides.cropY ?? current.cropY,
    rotation: overrides.rotation ?? current.rotation,
    flipH: overrides.flipH ?? current.flipH,
    flipV: overrides.flipV ?? current.flipV,
    sourceBlob: current.source,
    preserveResolution: overrides.preserveResolution,
  });
};

export const revokeSlotImage = (image: SlotImage | undefined) => {
  if (image?.previewUrl) URL.revokeObjectURL(image.previewUrl);
};

export const blobToUint8Array = async (blob: Blob): Promise<Uint8Array> => {
  return new Uint8Array(await blob.arrayBuffer());
};

export const triggerDownload = (bytes: Uint8Array, filename: string) => {
  // Copy into a fresh ArrayBuffer so the Blob constructor doesn't choke on
  // a SharedArrayBuffer-backed view in stricter type setups.
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  const blob = new Blob([buffer], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const timestampForFilename = () => {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  );
};
